import { toast, toastTypeEnum } from 'common/utils';
import { observable, action, makeObservable } from 'mobx';
import {
  getUserRegions,
  IAddUserRegionsApiData,
  addUserRegions,
  IEditUserRegionsApiData,
  editUserRegion,
  deleteRegion
} from 'services/userRegions';
import { getApiErrorMessage } from 'services/utils';
// import { loginService } from 'services/users';
import { RootStore } from 'stores';
import {
  IgetUserApiData,
  IUsersRegion,
  pagination,
  sortByParams
} from 'types/index';

export type IInitialState = {
  data: {
    userRegions: IUsersRegion[];
    total: number | null;
    getUserRegionsParams: sortByParams & pagination;
  };
  ui: {
    isUserRegionsLoading: boolean;
    disableUserRegionsLoading: null | string;
    editUserLoading: null | string;
    addUserLoading: boolean;
    deleteUserLoading: boolean;
  };
};

export const initialState: IInitialState = {
  data: {
    userRegions: [],
    total: null,
    getUserRegionsParams: { limit: 10, offset: 0 }
  },
  ui: {
    isUserRegionsLoading: false,
    disableUserRegionsLoading: null,
    editUserLoading: null,
    addUserLoading: false,
    deleteUserLoading: false
  }
};

export class UsersRegions {
  state: IInitialState = initialState;
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      state: observable,
      getUserRegions: action,
      addUserRegions: action,
      editUserRegions: action,
      deleteRegion: action,
      setState: action,
      reset: action
    });

    this.rootStore = rootStore;
    /* use this space to hydrate state from localStorage */
  }

  getUserRegions = async (params: IgetUserApiData, showLoading = true) => {
    try {
      showLoading &&
        this.setState({
          ui: { ...this.state.ui, isUserRegionsLoading: true }
        });

      if (!params?.search || params?.search.length === 0) {
        delete params.search;
      }
      const { data } = await getUserRegions({ ...params });

      this.setState({
        data: {
          ...this.state.data,
          userRegions: data.data.rows,
          total: data.data.count
        }
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.state.ui.isUserRegionsLoading = false;
    }
  };

  addUserRegions = async (
    params: IAddUserRegionsApiData
  ): Promise<boolean | undefined> => {
    try {
      this.setState({
        ui: { ...this.state.ui, addUserLoading: true }
      });
      await addUserRegions(params);

      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({
        ui: { ...this.state.ui, addUserLoading: false }
      });
    }
  };

  editUserRegions = async (params: IEditUserRegionsApiData) => {
    try {
      this.setState({
        ui: { ...this.state.ui, editUserLoading: params.id }
      });

      await editUserRegion(params);
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({
        ui: { ...this.state.ui, editUserLoading: null }
      });
    }
  };

  deleteRegion = async (values: string) => {
    try {
      this.setState({
        ui: { ...this.state.ui, deleteUserLoading: true }
      });
      await deleteRegion(values);
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({
        ui: { ...this.state.ui, deleteUserLoading: false }
      });
    }
  };

  setState = (params: Partial<IInitialState>) => {
    const { state } = this;
    this.state = {
      ...state,
      ...params
    };
    /* use this space to save/sync state with localStorage */
  };

  reset = () => {
    this.state = initialState;
    /* use this space to reset localStorage in case if you're persisting this state */
  };
}
